/**
 * The resolver: one route for every surface on the platform.
 *
 * The client router has no table of sections. It takes whatever path it is on,
 * asks the server what lives there, and renders the component for the template
 * name that comes back. Adding a surface to the platform is a catalogue row,
 * exactly as it is in the server rendered tier, and this application needs no
 * change to reach it.
 *
 * A refusal, a reason gate and a placeholder all arrive here as ordinary
 * answers with their own template names, so they render as pages rather than as
 * error states. That is the difference between a platform that explains itself
 * and one that shows a blank table when a permission is missing.
 */
import { useEffect } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { api, ApiError } from '../api/client'
import type { Dict } from '../api/types'
import { Surface } from '../screens/registry'
import { Denied, ReasonGate } from '../screens/Simple'
import { Note, Spinner } from '../components/bits'
import { MOUNT, appPath, platformPath } from './paths'

export function Resolver() {
  const location = useLocation()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const path = platformPath(location.pathname)
  const query = params.toString()

  const { data, error, isLoading } = useQuery({
    queryKey: ['page', path, query],
    queryFn: () => {
      const fields: Record<string, string> = {}
      params.forEach((value, key) => { fields[key] = value })
      return api.page(path, fields)
    },
    retry: false,
  })

  // The landing route is a server decision: it is the role's own dashboard, or
  // its first granted section when it has no dashboard. Resolving it here would
  // be a guess that went wrong for every role whose grants changed.
  useEffect(() => {
    if (data?.template === 'redirect') {
      navigate(appPath(String((data.data as { to: string }).to)), { replace: true })
    }
  }, [data, navigate])

  if (isLoading) return <Spinner what="this surface" />

  if (error) {
    const problem = error as ApiError
    if (problem.unauthenticated) {
      window.location.assign(`${MOUNT}/`)
      return null
    }
    // A refusal or a gate that came back as a rendered surface is rendered as
    // that surface, with everything the handler put in it. Only a bare problem
    // document falls through to the generic pages.
    const carried = problem.payload as { template?: string; data?: Dict } | null
    if (carried?.template) {
      return <Surface template={carried.template} data={carried.data || {}} />
    }
    if (problem.denied) return <Denied detail={problem.detail} />
    if (problem.needsReason) {
      return <ReasonGate data={{ target: path, section_key: '' }} />
    }
    return (
      <Note kind="bad">
        <b>{problem.title || 'That surface could not be read'}.</b>{' '}
        {problem.detail}
      </Note>
    )
  }

  if (!data || data.template === 'redirect') return <Spinner />

  return <Surface template={data.template} data={data.data} />
}
